import React, { useState, useRef } from "react";
import "../styles/MobileLogin.css";
import Advert from "./Advert";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../context/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styled from "styled-components";

export default function ResetPasswordScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { prefix, requestId, phoneNumber } = location.state || {};

  const [otp, setOtp] = useState(["", "", "", ""]);
  const [resetPassword, setResetPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const inputRefs = useRef([]);

  React.useEffect(() => {
    if (!prefix || !requestId || !phoneNumber) {
      toast.error("Invalid reset session. Please start over.");
      navigate("/password-reset");
      return;
    }

    // Start countdown timer
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [prefix, requestId, phoneNumber, navigate]);

  const handleOtpChange = (value, index) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    setIsLoading(true);
    try {
      const response = await api.post("/otp/send", {
        senderId: "Huntex",
        phoneNumber: phoneNumber,
        countryCode: "GH",
      });

      if (response.data.code === "0000") {
        toast.success("OTP resent successfully!");
        setResendTimer(60);
        setCanResend(false);
        
        // Restart timer
        const timer = setInterval(() => {
          setResendTimer((prev) => {
            if (prev <= 1) {
              setCanResend(true);
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        toast.error("Failed to resend OTP.");
      }
    } catch (error) {
      toast.error("Error resending OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyAndReset = async () => {
    const enteredOTP = otp.join("");

    if (enteredOTP.length !== 4) {
      toast.error("Please enter the 4-digit code.");
      return;
    }

    if (!resetPassword) {
      toast.error("Please enter a new password.");
      return;
    }

    if (resetPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    if (resetPassword.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    setIsLoading(true);

    try {
      // Verify OTP
      const verifyResponse = await api.post("/otp/verify", {
        requestId: requestId,
        prefix: prefix,
        code: enteredOTP,
      });

      if (verifyResponse.status === 200) {
        // Update password
        const updatePasswordResponse = await api.post("/updatePassword", {
          phoneNumber: phoneNumber,
          newPassword: resetPassword,
        });

        if (updatePasswordResponse.status === 200) {
          toast.success("Password reset successfully!");
          setTimeout(() => {
            navigate("/login");
          }, 2000);
        } else {
          toast.error("Password update failed. Please try again.");
        }
      } else {
        toast.error("Invalid verification code. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <div className="login-image-top"></div>
      <div className="login-box">
        <h1>Verify & Reset Password</h1>
        <p>Enter the 4-digit code sent to your phone and your new password</p>

        <form onSubmit={(e) => { e.preventDefault(); handleVerifyAndReset(); }}>
          {/* OTP Input */}
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", marginBottom: "10px", fontWeight: "600" }}>
              Verification Code
            </label>
            <OtpContainer>
              {otp.map((digit, index) => (
                <OtpInput
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                />
              ))}
            </OtpContainer>
          </div>

          {/* New Password */}
          <div className="input-group" style={{ marginBottom: "15px" }}>
            <input
              type="password"
              placeholder="New Password (min 6 characters)"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              required
            />
          </div>

          {/* Confirm Password */}
          <div className="input-group" style={{ marginBottom: "20px" }}>
            <input
              type="password"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          {/* Timer and Resend */}
          <TimerContainer>
            {canResend ? (
              <ResendButton type="button" onClick={handleResendOTP} disabled={isLoading}>
                Resend Code
              </ResendButton>
            ) : (
              <TimerText>Resend code in {resendTimer}s</TimerText>
            )}
          </TimerContainer>

          {/* Submit */}
          <button type="submit" disabled={isLoading}>
            {isLoading ? "Processing..." : "Reset Password"}
          </button>
        </form>

        {/* Links */}
        <div className="links">
          <span>
            Remember your password? <a href="#" onClick={() => navigate('/login')}>Login</a>
          </span>
        </div>

        <p className="support-text">
          Having a problem? <a href="#">Contact support</a>
        </p>
      </div>
      <div style={{ marginTop: "90px", width: "100%" }}>
        <Advert />
      </div>
    </div>
  );
}

const OtpContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const OtpInput = styled.input`
  width: 50px;
  height: 50px;
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #03750b;
    box-shadow: 0 0 0 3px rgba(3, 117, 11, 0.1);
  }
`;

const TimerContainer = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

const TimerText = styled.p`
  color: #666;
  font-size: 14px;
`;

const ResendButton = styled.button`
  background: transparent;
  color: #03750b;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #025a08;
  }

  &:disabled {
    color: #999;
    cursor: not-allowed;
  }
`;
